import axios from 'axios'

// Determine API base URL at runtime
// In production (Cloudflare Pages), always use relative /api path (handled by Cloudflare Functions)
// In development, use VITE_API_BASE_URL if set, otherwise default to /api (proxy)
const getApiBaseUrl = (): string => {
  // Always check runtime hostname first - this is the source of truth
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    const protocol = window.location.protocol
    
    // If we're on HTTPS (production), ALWAYS use /api (ignore any env vars)
    // This prevents CORS issues when production tries to access localhost
    if (protocol === 'https:') {
      return '/api'
    }
    
    // If we're on a production domain, ALWAYS use /api (ignore any env vars)
    if (hostname === 'open1031.pages.dev' || 
        hostname.endsWith('.pages.dev') ||
        hostname === 'open1031.ai' ||
        hostname.endsWith('.open1031.ai') ||
        hostname.includes('cloudflarepages.dev')) {
      return '/api'
    }
    
    // NEVER use localhost URLs in production - they cause CORS issues
    if (import.meta.env.PROD) {
      return '/api'
    }
    
    // If we're on localhost in dev, use /api (Vite proxy)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return '/api'
    }
  }
  
  // In development only, check if VITE_API_BASE_URL is set and not localhost
  // But only if we're not in production mode
  if (!import.meta.env.PROD) {
    const envUrl = import.meta.env.VITE_API_BASE_URL
    // NEVER use localhost URLs - they cause CORS issues
    if (envUrl && 
        !envUrl.includes('localhost') && 
        !envUrl.includes('127.0.0.1') &&
        !envUrl.startsWith('http://localhost') &&
        !envUrl.startsWith('https://localhost')) {
      return envUrl
    }
  }
  
  // Default to relative /api (will use Vite proxy in dev, Cloudflare Functions in prod)
  return '/api'
}

const USE_FUNCTIONS = import.meta.env.VITE_ENABLE_FUNCTIONS === 'true'

const api = axios.create({
  baseURL: '/api', // Default, will be overridden by interceptor
  headers: { 'Content-Type': 'application/json' },
})

// Ensure baseURL is always correct on each request
api.interceptors.request.use((config) => {
  // Recompute baseURL on each request to ensure it's always correct
  const baseURL = getApiBaseUrl()
  
  // Safety check: NEVER allow localhost URLs if we're on HTTPS (production)
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && baseURL.includes('localhost')) {
    console.warn('Blocked localhost URL in production, using /api instead')
    config.baseURL = '/api'
  } else {
    config.baseURL = baseURL
  }
  return config
})

export interface PropertyLookupResponse {
  address: string
  imageUrl: string
  price: number | string | null
  beds: number | null
  baths: number | null
  sqft: number | null
  zillowUrl: string
  redfinUrl: string
  source: 'rapidapi-zillow' | 'mock'
}

export const propertyApi = {
  async lookupByAddress(address: string): Promise<PropertyLookupResponse> {
    // In local dev, avoid hitting missing functions; return mock immediately unless explicitly enabled
    if (import.meta.env.DEV && !USE_FUNCTIONS) {
      return {
        address,
        imageUrl: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80&auto=format&fit=crop',
        price: 1048000,
        beds: 3,
        baths: 3,
        sqft: 1628,
        zillowUrl: `https://www.zillow.com/homes/${encodeURIComponent(address)}`,
        redfinUrl: `https://www.redfin.com/stingray/do/location-autocomplete?location=${encodeURIComponent(address)}`,
        source: 'mock'
      }
    }

    try {
      const resp = await api.get('/properties/lookup', { params: { address } })
      return resp.data
    } catch (e) {
      // Local dev fallback when backend functions aren't available
      return {
        address,
        imageUrl: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80&auto=format&fit=crop',
        price: 1048000,
        beds: 3,
        baths: 3,
        sqft: 1628,
        zillowUrl: `https://www.zillow.com/homes/${encodeURIComponent(address)}`,
        redfinUrl: `https://www.redfin.com/stingray/do/location-autocomplete?location=${encodeURIComponent(address)}`,
        source: 'mock'
      }
    }
  }
}


